import axios, { type AxiosError } from 'axios';

import getCookieToken from '@/hooks/get-cookie-token';
import rollbar from '@/lib/rollbar';
import { toCamelCase } from '@/utils/helpers/to-camel-case';
import { toSnakeCase } from '@/utils/helpers/to-snake-case';

const { VITE_APP_DOMAIN } = import.meta.env;

const BASE_API_URL = `${VITE_APP_DOMAIN}/api/v1`;

const axiosInstance = axios.create({
  baseURL: BASE_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- REQUEST INTERCEPTOR ---
// This runs BEFORE every request is sent.
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getCookieToken();

    if (token) {
      config.headers['x-csrf-token'] = token;
    }

    if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
      try {
        config.data = toSnakeCase(config.data);
      } catch (error) {
        rollbar.error('API Request Data Transformation Error', error as Error, {
          url: config.url,
        });
      }
    }

    if (config.params && typeof config.params === 'object') {
      try {
        config.params = toSnakeCase(config.params);
      } catch (error) {
        rollbar.error('API Request Params Transformation Error', error as Error, {
          url: config.url,
        });
      }
    }

    return config;
  },
  (error) => {
    rollbar.error('Axios Request Error', error);

    return Promise.reject(error);
  }
);

// --- RESPONSE INTERCEPTOR ---
// This runs AFTER a response is received. It handles both successful responses and errors.
axiosInstance.interceptors.response.use(
  (response) => {
    if (response.data) {
      try {
        response.data = toCamelCase(response.data);
      } catch (error) {
        rollbar.error('API Response Transformation Error', error as Error, {
          url: response.config.url,
        });
      }
    }

    return response;
  },

  async (error) => {
    const axiosError = error as AxiosError;

    if (axiosError.response) {
      const { status, data } = axiosError.response;
      const { config } = axiosError;

      const url = `${config?.baseURL ?? ''}${config?.url ?? ''}`;
      const method = config?.method?.toUpperCase();

      const isAuthCheck = url.includes('/auth/me') && (status === 401 || status === 403);

      if (isAuthCheck) {
        return Promise.reject(error);
      }

      if (status >= 500) {
        rollbar.error('API Server Error', {
          status,
          method,
          url,
          data,
        });
      } else if (status !== 404 && status !== 401 && status !== 403) {
        rollbar.info('API Client Error', {
          status,
          method,
          url,
          data,
        });
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
